import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
  MarkerClusterer,
} from "@react-google-maps/api";
import config from "../../config.js";
import { useSettings } from "../../context/SettingsContext";
import { toast } from "react-toastify";
import {
  normalizeDeviceData,
  determineVehicleDirection,
  upsertDirectionForVehicle,
  rehydrateDirections,
  hashString,
  DirectionsCache,
  createCacheKey,
  DirectionStateCache,
} from "./mapConstants.js";
import {
  getPlaceConfig,
  TransportIcons,
  mapStyles,
  colors,
  containerStyle,
  normalizeMapView,
} from "./functions.js";
import { useStopRedirectEnhanced } from "../../hooks/useUserInfo.js";
import { getVehicleDirection, updateVehicleDirection } from "./dashboardAPI.js";
import { fitMapToStops } from "./mapUtils.js";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance.js";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import {
  computeVehicleLabelOffsets,
  computeStopLabelOffsets,
} from "../../utils/mapLabelOffsets.js";
import MapLegend from "./MapLegend.jsx";

const libraries = ["places", "directions"];

const findNearestPointOnRoute = (position, directions) => {
  if (!position || !directions?.routes?.[0]) return null;
  const route = directions.routes[0];
  let closestPoint = null;
  let minDistance = Infinity;
  let routeIndex = -1;
  let currentRouteIndex = 0;

  route.legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      const path = step.path;
      path.forEach((point, index) => {
        if (index === 0) return;
        const getLat = (p) => (typeof p.lat === "function" ? p.lat() : p.lat);
        const getLng = (p) => (typeof p.lng === "function" ? p.lng() : p.lng);

        const start = {
          lat: getLat(path[index - 1]),
          lng: getLng(path[index - 1]),
        };
        const end = { lat: getLat(point), lng: getLng(point) };

        const dx = end.lng - start.lng;
        const dy = end.lat - start.lat;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        if (segmentLength === 0) return;

        const t =
          ((position.lng - start.lng) * dx + (position.lat - start.lat) * dy) /
          (segmentLength * segmentLength);

        if (t < 0) {
          const distToStart = calculateDistance(position, start);
          if (distToStart < minDistance) {
            minDistance = distToStart;
            closestPoint = start;
            routeIndex = currentRouteIndex;
          }
          return;
        }

        if (t > 1) {
          const distToEnd = calculateDistance(position, end);
          if (distToEnd < minDistance) {
            minDistance = distToEnd;
            closestPoint = end;
            routeIndex = currentRouteIndex + 1;
          }
          return;
        }

        const projectedPoint = {
          lat: start.lat + t * dy,
          lng: start.lng + t * dx,
        };
        const distance = calculateDistance(position, projectedPoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = projectedPoint;
          routeIndex = currentRouteIndex + t;
        }

        currentRouteIndex++;
      });
    });
  });

  const MAX_SNAP_DISTANCE = 50;
  if (minDistance <= MAX_SNAP_DISTANCE) {
    return { ...closestPoint, routeIndex: routeIndex };
  }
  return null;
};

function Map({ stops, routes, vehicles }) {
  // console.log("Rendering DashboardMap with", { stops, routes, vehicles });

  const { settings, loading } = useSettings();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const { redirectToStopTrack, orgId } = useStopRedirectEnhanced();
  const mapDetailsArray = settings.mapDetails || [];
  const [map, setMap] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [directions, setDirections] = useState([]);
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [snappedPositions, setSnappedPositions] = useState({});
  const [vehicleDirections, setVehicleDirections] = useState({});
  const [placeMarkers, setPlaceMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [vehicleStatuses, setVehicleStatuses] = useState({});

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const vehicleDetailsHoverTimeoutRef = useRef(null);
  const hoveredVehicleIdRef = useRef(null);
  const visibleVehicleIds = useMemo(
    () =>
      new Set(vehicles.map((vehicle) => vehicle?.device_id).filter(Boolean)),
    [vehicles],
  );
  const visibleVehicleLocations = useMemo(
    () =>
      vehicleLocations.filter((vehicle) =>
        visibleVehicleIds.has(vehicle?.deviceId),
      ),
    [vehicleLocations, visibleVehicleIds],
  );
  // Cache: device_id → resolved image blob URL
  const vehicleImageCacheRef = useRef({});

  // ─── Label collision offsets ─────────────────────────────────────────────────
  const labelOffsets = useMemo(() => {
    if (!map || !visibleVehicleLocations.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = visibleVehicleLocations.map((v) => {
      const vData = vehicles.find((vv) => vv.device_id === v.deviceId) || {};
      const direction = vehicleDirections[v.deviceId];
      const hasArrow = !!(direction && direction !== "unknown");
      return {
        deviceId: v.deviceId,
        lat: parseFloat(v.latitude),
        lng: parseFloat(v.longitude),
        name: vData.name || "",
        hasArrow,
      };
    });
    return computeVehicleLabelOffsets(projection, map.getZoom(), entries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, visibleVehicleLocations, zoom, vehicleDirections]);

  // ─── Stop label collision offsets ────────────────────────────────────────────
  const stopLabelOffsets = useMemo(() => {
    if (!map || !stops.length) return {};
    const projection = map.getProjection();
    if (!projection) return {};
    const entries = stops.map((s) => ({
      id: s.id,
      lat: parseFloat(s.latitude),
      lng: parseFloat(s.longitude),
      name: s.name,
    }));
    const z = map.getZoom() ?? 14;
    // BASE_OFFSET = icon height (40px) + 4px gap so label sits just above the icon
    return computeStopLabelOffsets(projection, z, entries, {
      BASE_OFFSET: 44,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, stops, zoom]);
  const isMountedRef = useRef(true);
  const eventSourcesRef = useRef({});
  const lastDataReceivedRef = useRef({});
  const [animatedVehicles, setAnimatedVehicles] = useState({});
  const positionHistoryRef = useRef({});
  const lastKnownHeadings = useRef({});
  const MIN_MOVEMENT_THRESHOLD = 5;
  const MAX_HISTORY_LENGTH = 5;
  const deviceIdToVehicles_color = useRef({}); // Map deviceId to vehicle color

  const { getIntermediateStopIcon, getVehicleIcon: getCustomVehicleIcon } =
    useMapIcons(settings, vehicles, stops, routes);

  // Populate deviceIdToVehicles_color
  useEffect(() => {
    const colorMap = {};
    vehicles.forEach((vehicle) => {
      colorMap[vehicle.device_id] = vehicle.vehicle_color || "green";
    });
    deviceIdToVehicles_color.current = colorMap;
  }, [vehicles]);

  useEffect(() => {
    setVehicleLocations((prev) =>
      prev.filter((vehicle) => visibleVehicleIds.has(vehicle?.deviceId)),
    );
    setSnappedPositions((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([deviceId]) => visibleVehicleIds.has(deviceId)),
      ),
    );
    setVehicleDirections((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([deviceId]) => visibleVehicleIds.has(deviceId)),
      ),
    );
    setVehicleStatuses((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([deviceId]) => visibleVehicleIds.has(deviceId)),
      ),
    );
    setSelectedVehicle((prev) =>
      prev && !visibleVehicleIds.has(prev.deviceId) ? null : prev,
    );
  }, [visibleVehicleIds]);

  const mapType = normalizeMapView(settings.mapview);

  const openVehicleDetails = useCallback(
    (vehicle) => {
      const vehicleData =
        vehicles.find((v) => v?.device_id === vehicle?.deviceId) || {};
      const cachedUrl =
        vehicleImageCacheRef.current[vehicleData.device_id] || null;
      const vehicleRoute = routes.find(
        (route) => route.id === vehicleData?.routeId,
      );
      const vehicleStatus =
        vehicleStatuses[vehicle.deviceId] || vehicleData.status || "ACTIVE";

      // ── Look inside documents array for VehicleImage ──
      const imageDoc = vehicleData.documents?.find(
        (doc) => doc.name === "VehicleImage",
      );
      const rawImagePath = imageDoc?.location || null;

      let resolvedImageUrl = cachedUrl;
      if (!resolvedImageUrl && rawImagePath) {
        resolvedImageUrl = rawImagePath.startsWith("http")
          ? rawImagePath
          : `${config.baseURL}/upload/image?fileName=${encodeURIComponent(rawImagePath)}`;
      }

      setSelectedVehicle({
        ...vehicle,
        name: vehicleData.name || "Unknown Vehicle",
        speed: vehicle.speed || vehicle.Speed || "0",
        routeName: vehicleRoute?.name || vehicleRoute?.routeName || "N/A",
        imageUrl: resolvedImageUrl,
        color: vehicleData.vehicle_color || "#008C23",
        status: vehicleStatus,
      });
    },
    [routes, vehicleStatuses, vehicles],
  );

  useEffect(() => {
    return () => {
      if (vehicleDetailsHoverTimeoutRef.current) {
        clearTimeout(vehicleDetailsHoverTimeoutRef.current);
      }
    };
  }, []);

  // Pre-fetch and cache vehicle images so the modal shows instantly without repeat network calls
  useEffect(() => {
    if (!vehicles.length) return;
    const cache = vehicleImageCacheRef.current;

    vehicles.forEach((vehicle) => {
      const deviceId = vehicle.device_id;

      // ── Look inside documents array for VehicleImage ──
      const imageDoc = vehicle.documents?.find(
        (doc) => doc.name === "VehicleImage",
      );
      const rawPath = imageDoc?.location || null;

      if (!rawPath || cache[deviceId]) return;

      const imageUrl = rawPath.startsWith("http")
        ? rawPath
        : `${config.baseURL}/upload/image?fileName=${encodeURIComponent(rawPath)}`;

      fetch(imageUrl, { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (!cache[deviceId]) {
            cache[deviceId] = URL.createObjectURL(blob);
          }
        })
        .catch(() => {});
    });

    return () => {
      if (!isMountedRef.current) {
        Object.values(vehicleImageCacheRef.current).forEach((url) =>
          URL.revokeObjectURL(url),
        );
        vehicleImageCacheRef.current = {};
      }
    };
  }, [vehicles]);

  // Fetch places using Places API
  useEffect(() => {
    if (!isLoaded || !map || !mapDetailsArray.length) {
      setPlaceMarkers([]);
      return;
    }

    // Add a small delay to ensure map is fully initialized
    const searchTimeout = setTimeout(() => {
      // Safety check: ensure map center is available
      const mapCenter = map.getCenter();
      if (!mapCenter) {
        console.warn("Map center not available yet, skipping places search");
        return;
      }

      const placesService = new window.google.maps.places.PlacesService(map);
      const placeTypes = mapDetailsArray.filter(
        (detail) => detail !== "STREET_NAMES",
      );

      // Clear existing markers first
      setPlaceMarkers([]);

      // If no place types selected, exit early
      if (placeTypes.length === 0) {
        console.log("No place types selected");
        return;
      }

      // Safely get lat/lng values
      let centerLat, centerLng;
      try {
        centerLat = mapCenter.lat();
        centerLng = mapCenter.lng();

        // Validate the values
        if (
          typeof centerLat !== "number" ||
          typeof centerLng !== "number" ||
          isNaN(centerLat) ||
          isNaN(centerLng)
        ) {
          console.warn("Invalid map center coordinates:", {
            centerLat,
            centerLng,
          });
          return;
        }
      } catch (error) {
        console.error("Error getting map center:", error);
        return;
      }

      // Log the map center and search details
      console.log("Searching for places near:", {
        lat: centerLat,
        lng: centerLng,
        radius: 20000,
        placeTypes,
      });

      // Collect all place results
      const allPlacePromises = placeTypes.map((placeType) => {
        return new Promise((resolve) => {
          const config = getPlaceConfig(placeType);
          if (!config || !config.type) {
            console.warn(`No config found for place type: ${placeType}`);
            resolve([]);
            return;
          }

          const request = {
            location: new window.google.maps.LatLng(centerLat, centerLng),
            radius: 20000,
            type: config.type,
          };

          console.log(`Searching for ${placeType} (${config.type})...`);

          placesService.nearbySearch(request, (results, status) => {
            console.log(
              `${placeType} search result:`,
              status,
              results?.length || 0,
              "places",
            );

            if (status === "OK" && results) {
              // Transform results into marker objects
              const markers = results.map((place) => ({
                id: place.place_id,
                name: place.name,
                position: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                },
                type: placeType,
              }));
              console.log(`Found ${markers.length} ${placeType} markers`);
              resolve(markers);
            } else if (status === "ZERO_RESULTS") {
              console.warn(`No ${placeType} found in search area`);
              resolve([]);
            } else {
              console.error(`Places search failed for ${placeType}:`, status);
              resolve([]);
            }
          });
        });
      });

      // Wait for all searches to complete and combine results
      Promise.all(allPlacePromises).then((allMarkers) => {
        const flattenedMarkers = allMarkers.flat();
        console.log(
          `Total place markers loaded: ${flattenedMarkers.length}`,
          flattenedMarkers,
        );
        setPlaceMarkers(flattenedMarkers);
      });
    }, 300); // 300ms delay to ensure map is ready

    // Cleanup timeout on unmount or dependency change
    return () => clearTimeout(searchTimeout);
  }, [isLoaded, map, mapDetailsArray]);

  const getMapOptions = useCallback(() => {
    const showStreetNames = mapDetailsArray.includes("STREET_NAMES");

    const baseOptions = {
      mapTypeId: mapType,
      styles: [
        ...mapStyles, // Include base styles (all off, road geometry on)
        {
          featureType: "road",
          elementType: "labels.text",
          stylers: [{ visibility: showStreetNames ? "on" : "off" }],
        },
        {
          featureType: "water",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text",
          stylers: [{ visibility: "on" }],
        },
      ],
    };

    return baseOptions;
  }, [mapType, mapDetailsArray]);

  useEffect(() => {
    const deviceIds = vehicles.map((v) => v.device_id);
    setVehicleIds(deviceIds);
  }, [vehicles]);

  const fetchInitialLocations = useCallback(async () => {
    if (!vehicleIds.length) return;
    try {
      const promises = vehicleIds.map(async (deviceId) => {
        const response = await fetch(
          `${config.baseURL}/location/latest/${deviceId}`,
        );
        const data = await response.json();
        if (data && data.latitude != null && data.longitude != null) {
          const normalizedData = normalizeDeviceData(data);
          const vehicleData = vehicles.find((v) => v.device_id === deviceId);
          const route = routes.find((r) => r.id === vehicleData?.routeId);
          const direction = directions.find(
            (d) => d.routeColor === route?.routeColor,
          );

          let snappedPosition = null;
          if (direction) {
            const position = {
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            };
            snappedPosition = findNearestPointOnRoute(position, direction);
          }

          positionHistoryRef.current[deviceId] = [
            {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
              timestamp: Date.now(),
            },
          ];
          const storedData = JSON.parse(localStorage.getItem(deviceId) || "{}");
          const initialHeading = storedData.lastHeading || 0;
          lastKnownHeadings.current[deviceId] = initialHeading;

          return {
            ...normalizedData,
            latitude: snappedPosition
              ? snappedPosition.lat
              : normalizedData.latitude,
            longitude: snappedPosition
              ? snappedPosition.lng
              : normalizedData.longitude,
            heading: initialHeading,
          };
        } else {
          console.warn(`No valid location data for device ${deviceId}`, data);
          return null;
        }
      });
      const locations = await Promise.all(promises);
      const validLocations = locations.filter((loc) => loc !== null);
      setVehicleLocations(validLocations);

      const newSnappedPositions = {};
      validLocations.forEach((loc) => {
        const vehicleData = vehicles.find((v) => v.device_id === loc.deviceId);
        const route = routes.find((r) => r.id === vehicleData?.routeId);
        const direction = directions.find(
          (d) => d.routeColor === route?.routeColor,
        );
        if (direction) {
          const position = {
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
          };
          const snapped = findNearestPointOnRoute(position, direction);
          if (snapped) {
            newSnappedPositions[loc.deviceId] = snapped;
          }
        }
      });
      setSnappedPositions(newSnappedPositions);

      if (!isMountedRef.current) return;

      setVehicleLocations(validLocations);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [vehicleIds, vehicles, routes, directions]);

  const resolveTrendDirectionForVehicle = useCallback(
    (deviceId) => {
      const liveVehicle = vehicleLocations.find(
        (vehicle) => vehicle?.deviceId === deviceId,
      );
      const cachedState = DirectionStateCache.loadDirectionState(deviceId);
      const recentPosition =
        cachedState?.positions?.[cachedState.positions.length - 1] ||
        cachedState?.lastMovedPosition ||
        null;

      const currentPosition = liveVehicle
        ? {
            latitude: parseFloat(liveVehicle.latitude),
            longitude: parseFloat(liveVehicle.longitude),
          }
        : recentPosition;

      if (
        !currentPosition ||
        Number.isNaN(parseFloat(currentPosition.latitude)) ||
        Number.isNaN(parseFloat(currentPosition.longitude))
      ) {
        return "unknown";
      }

      return determineVehicleDirection(
        deviceId,
        currentPosition,
        stops,
        routes,
        vehicles,
      );
    },
    [vehicleLocations, stops, routes, vehicles],
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (vehicleIds.length) {
      fetchInitialLocations();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [vehicleIds, fetchInitialLocations]);

  useEffect(() => {
    if (isLoading || !vehicleIds.length) return;
    const fetchDirectionsFromRedis = async () => {
      try {
        const directions = {};
        const missingDirectionIds = [];
        await Promise.all(
          vehicleIds.map(async (deviceId) => {
            const dir = await getVehicleDirection(deviceId);
            const cachedState = DirectionStateCache.loadDirectionState(deviceId);

            if (dir && dir !== "unknown") {
              DirectionStateCache.saveDirectionState(
                deviceId,
                dir,
                cachedState?.positions || [],
                cachedState?.lastMovedPosition || null,
              );
              directions[deviceId] = dir;
            } else {
              missingDirectionIds.push(deviceId);
            }
          }),
        );

        const prioritizedMissingDirectionIds = missingDirectionIds.sort(
          (leftId, rightId) => {
            const leftState = DirectionStateCache.loadDirectionState(leftId);
            const rightState = DirectionStateCache.loadDirectionState(rightId);
            const leftMissing = !leftState?.direction || leftState.direction === "unknown";
            const rightMissing =
              !rightState?.direction || rightState.direction === "unknown";

            if (leftMissing === rightMissing) return 0;
            return leftMissing ? -1 : 1;
          },
        );

        await Promise.all(
          prioritizedMissingDirectionIds.map(async (deviceId) => {
            const trendDirection = resolveTrendDirectionForVehicle(deviceId);

            if (!trendDirection || trendDirection === "unknown") {
              return;
            }

            directions[deviceId] = trendDirection;

            try {
              await updateVehicleDirection(deviceId, trendDirection);
            } catch (syncError) {
              console.error(
                `Failed to sync inferred direction for ${deviceId}:`,
                syncError,
              );
            }
          }),
        );

        setVehicleDirections((prev) => ({ ...prev, ...directions }));
      } catch (error) {
        console.error("Error fetching directions from Redis:", error);
      }
    };

    fetchDirectionsFromRedis();
    const intervalId = setInterval(fetchDirectionsFromRedis, 60000);
    return () => clearInterval(intervalId);
  }, [vehicleIds, isLoading, resolveTrendDirectionForVehicle]);

  useEffect(() => {
    if (isLoading || !vehicleIds.length) return;

    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const eventSource = new EventSource(sseUrl);
    eventSourcesRef.current = { main: eventSource };

    // Helper: Validate position has valid numbers
    const isValidPosition = (pos) =>
      pos &&
      typeof pos.lat === "number" &&
      typeof pos.lng === "number" &&
      !isNaN(pos.lat) &&
      !isNaN(pos.lng);

    eventSource.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "connected") {
          console.log("Received connection confirmation:", data);
          return;
        }

        const normalizedData = normalizeDeviceData(data);

        // Skip if missing critical fields
        if (
          !normalizedData.deviceId ||
          normalizedData.latitude == null ||
          normalizedData.longitude == null
        ) {
          console.warn(
            "Skipping invalid data (missing ID or coords):",
            normalizedData,
          );
          return;
        }

        const lat = parseFloat(normalizedData.latitude);
        const lng = parseFloat(normalizedData.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          console.warn("Invalid latitude/longitude values:", {
            lat,
            lng,
            normalizedData,
          });
          return;
        }

        lastDataReceivedRef.current[normalizedData.deviceId] = Date.now();
        setVehicleStatuses((prev) => ({
          ...prev,
          [normalizedData.deviceId]: "ACTIVE",
        }));

        const vehicleData = vehicles.find(
          (v) => v.device_id === normalizedData.deviceId,
        );
        const route = routes.find((r) => r.id === vehicleData?.routeId);
        const direction = directions.find(
          (d) => d.routeColor === route?.routeColor,
        );

        let snappedPosition = null;
        if (direction) {
          const position = { lat, lng };
          snappedPosition = findNearestPointOnRoute(position, direction);
        }

        const newPosition = {
          lat: snappedPosition ? snappedPosition.lat : lat,
          lng: snappedPosition ? snappedPosition.lng : lng,
        };

        // Only push if position is valid
        if (isValidPosition(newPosition)) {
          if (!positionHistoryRef.current[normalizedData.deviceId]) {
            positionHistoryRef.current[normalizedData.deviceId] = [];
          }

          positionHistoryRef.current[normalizedData.deviceId].push({
            lat: newPosition.lat,
            lng: newPosition.lng,
            timestamp: Date.now(),
          });

          // Keep only last N entries
          const history = positionHistoryRef.current[normalizedData.deviceId];
          if (history.length > MAX_HISTORY_LENGTH) {
            positionHistoryRef.current[normalizedData.deviceId] =
              history.slice(-MAX_HISTORY_LENGTH);
          }
        } else {
          console.warn(
            "Invalid snapped or raw position, not adding to history:",
            {
              snappedPosition,
              raw: { lat, lng },
              deviceId: normalizedData.deviceId,
            },
          );
        }

        // === Calculate heading from movement ===
        let heading = lastKnownHeadings.current[normalizedData.deviceId] || 0;
        const history =
          positionHistoryRef.current[normalizedData.deviceId] || [];

        if (history.length >= 2) {
          const prevPos = history[history.length - 2];
          const currPos = history[history.length - 1];

          if (isValidPosition(prevPos) && isValidPosition(currPos)) {
            const distMoved = calculateDistance(prevPos, currPos);

            // console.log(`Vehicle ${normalizedData.deviceId} moved`, {
            //   meters: distMoved != null ? distMoved.toFixed(2) : "0.00",
            //   from: {
            //     lat: prevPos.lat.toFixed(6),
            //     lng: prevPos.lng.toFixed(6),
            //   },
            //   to: { lat: currPos.lat.toFixed(6), lng: currPos.lng.toFixed(6) },
            // });

            if (distMoved != null && distMoved > MIN_MOVEMENT_THRESHOLD) {
              heading = calculateBearing(
                { latitude: prevPos.lat, longitude: prevPos.lng },
                { latitude: currPos.lat, longitude: currPos.lng },
              );
              lastKnownHeadings.current[normalizedData.deviceId] = heading;
            }
          } else {
            console.warn(
              "Invalid positions in history, skipping heading update",
              {
                prevPos,
                currPos,
                deviceId: normalizedData.deviceId,
              },
            );
          }
        }

        // Persist heading
        const persisted = JSON.parse(
          localStorage.getItem(normalizedData.deviceId) || "{}",
        );
        persisted.lastHeading = heading;
        localStorage.setItem(
          normalizedData.deviceId,
          JSON.stringify(persisted),
        );

        // Update vehicle locations
        setVehicleLocations((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.deviceId === normalizedData.deviceId,
          );
          const updatedData = {
            ...normalizedData,
            latitude: newPosition.lat,
            longitude: newPosition.lng,
            heading,
          };

          if (existingIndex >= 0) {
            const newLocations = [...prev];
            newLocations[existingIndex] = updatedData;
            return newLocations;
          }
          return [...prev, updatedData];
        });

        // Update snapped positions
        setSnappedPositions((prev) => ({
          ...prev,
          [normalizedData.deviceId]: snappedPosition,
        }));

        // Update direction logic
        await upsertDirectionForVehicle({
          deviceId: normalizedData.deviceId,
          currentPosition: { lat, lng },
          stops,
          routes,
          vehicles,
          setVehicleDirections: (updater) => {
            setVehicleDirections((prev) => {
              const nextState =
                typeof updater === "function" ? updater(prev) : updater;
              const newDir = nextState[normalizedData.deviceId];
              const oldDir = prev[normalizedData.deviceId];
              if (newDir === "unknown" && oldDir && oldDir !== "unknown") {
                return prev;
              }
              return nextState;
            });
          },
          onDirectionChange: async (id, dir) => {
            await updateVehicleDirection(id, dir);
          },
        });
      } catch (error) {
        console.error("Error processing SSE data:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE connection error:", error);
      if (eventSourcesRef.current.main) {
        eventSourcesRef.current.main.close();
        delete eventSourcesRef.current.main;
      }
    };

    return () => {
      if (eventSourcesRef.current.main) {
        eventSourcesRef.current.main.close();
        eventSourcesRef.current = {};
      }
    };
  }, [vehicleIds, isLoading, stops, routes, vehicles]);

  const onLoad = useCallback(
    (map) => {
      setMap(map);
      fitMapToStops(map, stops, visibleVehicleLocations, snappedPositions);
    },
    [stops, visibleVehicleLocations, snappedPositions],
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (isLoaded && routes.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();

      const fetchDirections = async () => {
        const directionsPromises = routes.map((route) => {
          const cacheKey = createCacheKey(route, stops);
          return new Promise((resolve, reject) => {
            const cachedDirections = DirectionsCache.get(cacheKey);
            if (cachedDirections) {
              resolve({
                ...cachedDirections,
                routeColor: route.routeColor || cachedDirections.routeColor,
              });
              return;
            }

            console.log(
              `[Directions Cache] No valid cache for route ${route.id}, fetching from Maps API`,
            );

            const waypoints = route.routeCoordinates
              .slice(1, -1)
              .map((coord) => ({
                location: new window.google.maps.LatLng(
                  parseFloat(coord.latitude),
                  parseFloat(coord.longitude),
                ),
                stopover: coord.isStop || false,
              }));

            const origin = new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude),
            );

            const destination = new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .latitude,
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1]
                  .longitude,
              ),
            );

            directionsService.route(
              {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  console.log(
                    `[Directions Cache] Successfully fetched from Maps API for route ${route.id}`,
                  );
                  DirectionsCache.set(cacheKey, result, route.routeColor);
                  resolve({ ...result, routeColor: route.routeColor });
                } else {
                  console.error(
                    `[Directions Cache] Maps API error for route ${route.id}: ${status}`,
                  );
                  reject(new Error(`Directions API error: ${status}`));
                }
              },
            );
          });
        });

        try {
          const results = await Promise.all(directionsPromises);
          const validResults = results.filter(
            (result) =>
              result &&
              result.routes &&
              result.routes.length > 0 &&
              result.routes[0].legs &&
              result.routes[0].legs.length > 0,
          );
          setDirections(validResults);
          console.log(
            `[Directions Cache] Loaded ${validResults.length} valid route directions`,
          );
        } catch (error) {
          console.error("Error fetching directions:", error);
          setDirections([]);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, routes, map, stops]);

  useEffect(() => {
    const statusCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeoutThreshold = 5 * 60 * 1000;
      setVehicleStatuses((prev) => {
        const newStatuses = { ...prev };
        vehicleIds.forEach((deviceId) => {
          const lastReceived = lastDataReceivedRef.current[deviceId];
          if (!lastReceived || now - lastReceived > timeoutThreshold) {
            newStatuses[deviceId] = "DEAD";
          }
        });
        return newStatuses;
      });
    }, 180000);
    return () => clearInterval(statusCheckInterval);
  }, [vehicleIds]);

  const getDirectionArrow = (deviceId) => {
    const direction = vehicleDirections[deviceId];
    // console.log("Direction for",  direction);
    if (!direction || direction === "unknown") return "";
    let dir = true
      ? direction === "source_to_dest"
        ? " ➡️"
        : " ⬅️"
      : direction === "source_to_dest"
        ? " ⬅️"
        : " ➡️";

    return dir;
  };

  const getVehicleIcon = (deviceId, heading) => {
    const color = deviceIdToVehicles_color.current[deviceId] || "green";
    const direction = vehicleDirections[deviceId];
    // console.log("Direction for",  direction);
    if (!direction || direction === "unknown") return "";
    const adjustedHeading =
      direction !== "source_to_dest" ? (heading + 180) % 360 : heading;
    let head = false ? adjustedHeading + 90 : heading + 90;
    const rotatedSVG = getRightToLeftBusIcon(color, head).trim();
    const encodedSVG = encodeURIComponent(rotatedSVG);

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodedSVG}`,
      scaledSize: new window.google.maps.Size(50, 50), // 60px SVG canvas scaled for display
      anchor: new window.google.maps.Point(25, 25), // Centre of 50×50 display size
    };
  };

  const vehicleLegendIconUrl = useMemo(() => {
    const svg = getRightToLeftBusIcon("green", 180).trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }, []);

  const routeLegendColors = useMemo(
    () => directions.map((direction) => direction.routeColor || "#000000"),
    [directions],
  );

  const placeLegendItems = useMemo(
    () =>
      [...new Set(placeMarkers.map((place) => place.type))].map((type) => {
        const cfg = getPlaceConfig(type);
        return { type, icon: cfg?.icon };
      }),
    [placeMarkers],
  );

  const stopLegendItems = useMemo(() => {
    if (!isLoaded || !window.google?.maps) {
      return [{ label: "Stop", color: "#d32f2f" }];
    }
    const stopIcon = getIntermediateStopIcon();
    return [{ label: "Stop", iconUrl: stopIcon?.url }];
  }, [getIntermediateStopIcon, isLoaded]);

  const handleStopClick = (stop) => {
    try {
      if (!stop || !stop.id) {
        toast.error("Invalid stop data");
        return;
      }
      if (!orgId) {
        toast.error(
          "Organization ID not found. Please check your login status.",
        );
        return;
      }
      console.log(
        `Attempting to redirect to stop ${stop.id} with org ${orgId}`,
      );
      const success = redirectToStopTrack(stop.id);
      if (!success) {
        toast.error("Failed to redirect to stop tracking page");
      } else {
        toast.info(
          `Redirecting to track stop: ${stop.name || `Stop ${stop.id}`}`,
        );
      }
    } catch (error) {
      console.error("Error handling stop click:", error);
      toast.error("An error occurred while redirecting");
    }
  };

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return <div>Error loading maps</div>;
  }
  if (!isLoaded || loading) {
    return (
      <div style={{ padding: "40px" }}>
        <div height="40vh" width="100%" />
      </div>
    );
  }
  return (
    <>
      <div style={{ padding: "40px" }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={getMapOptions()}
          onZoomChanged={() => {
            if (map) setZoom(map.getZoom());
          }}
        >
          {placeMarkers.map((place) => {
            const config = getPlaceConfig(place.type);
            if (!config || !config.icon) {
              console.warn(`Missing config for place marker:`, place);
              return null;
            }

            return (
              <React.Fragment key={place.id}>
                <Marker
                  position={place.position}
                  title={place.name}
                  icon={{
                    url: config.icon,
                    scaledSize: new window.google.maps.Size(32, 32),
                  }}
                />
              </React.Fragment>
            );
          })}

          <MarkerClusterer
            options={(() => {
              const stopIcon = getIntermediateStopIcon();
              const clusterStyle = stopIcon
                ? {
                    url: stopIcon.url,
                    width: 46,
                    height: 46,
                    textColor: "#000000ff",
                    textSize: 11,
                    fontWeight: "bold",
                    anchorIcon: [46, 23],
                    anchorText: [-2, 0],
                  }
                : {
                    url:
                      "data:image/svg+xml;charset=UTF-8," +
                      encodeURIComponent(
                        `<svg xmlns="http://www.w3.org/2000/svg" width="46" height="46">
                          <circle cx="23" cy="23" r="20" fill="#d32f2f" stroke="#fff" stroke-width="2"/>
                        </svg>`,
                      ),
                    width: 46,
                    height: 46,
                    textColor: "#ffffff",
                    textSize: 12,
                    fontWeight: "bold",
                    anchorIcon: [23, 23],
                    anchorText: [-28, 0],
                  };
              return {
                styles: [clusterStyle, clusterStyle, clusterStyle],
                gridSize: 31,
                minimumClusterSize: 2,
                zoomOnClick: true,
              };
            })()}
          >
            {(clusterer) =>
              stops.map((stop) => (
                <React.Fragment key={stop.id}>
                  {/* Only show name label when not clustered (zoom >= 14) */}
                  {(zoom === null || zoom >= 14) &&
                    (() => {
                      const offsetPx = stopLabelOffsets[stop.id]?.topPx ?? 44;
                      const STOP_ICON_H = 40;
                      // Overlap the line slightly into the label (3px) so there's no gap
                      const LABEL_OVERLAP = 3;
                      // Connector: top overlaps label bottom by LABEL_OVERLAP,
                      // bottom sits at the icon top edge (STOP_ICON_H above pin-tip).
                      const connectorH = offsetPx - STOP_ICON_H + LABEL_OVERLAP;
                      return (
                        <OverlayView
                          position={{
                            lat: parseFloat(stop.latitude),
                            lng: parseFloat(stop.longitude),
                          }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          {/* Container anchored at pin tip */}
                          <div
                            style={{
                              position: "relative",
                              left: "0px",
                              top: "0px",
                            }}
                          >
                            {/* Label – centred horizontally, floated above icon */}
                            <div
                              style={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bottom: `${offsetPx}px`,
                                backgroundColor: "white",
                                color: "black",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "500",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                whiteSpace: "nowrap",
                                zIndex: 1001,
                                pointerEvents: "none",
                              }}
                            >
                              {stop.name}
                            </div>

                            {/* Connector line: overlaps label bottom slightly, ends at icon top */}
                            {connectorH > 0 && (
                              <svg
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  // bottom of SVG = icon top edge; line extends upward past label bottom
                                  bottom: `${STOP_ICON_H}px`,
                                  pointerEvents: "none",
                                  overflow: "visible",
                                  zIndex: 1000,
                                }}
                                width="2"
                                height={connectorH}
                              >
                                <line
                                  x1="1"
                                  y1="0"
                                  x2="1"
                                  y2={connectorH}
                                  stroke="rgba(0,0,0,0.45)"
                                  strokeWidth="1.5"
                                  strokeDasharray="3 2"
                                />
                              </svg>
                            )}
                          </div>
                        </OverlayView>
                      );
                    })()}
                  <Marker
                    position={{
                      lat: parseFloat(stop.latitude),
                      lng: parseFloat(stop.longitude),
                    }}
                    onClick={() => handleStopClick(stop)}
                    options={{ cursor: "pointer" }}
                    title={stop.name}
                    icon={getIntermediateStopIcon() || undefined}
                    clusterer={clusterer}
                  />
                </React.Fragment>
              ))
            }
          </MarkerClusterer>

          {visibleVehicleLocations?.map((vehicle) => {
            const vehicleData =
              vehicles.find((v) => v?.device_id === vehicle?.deviceId) || {};
            const status = false
              ? vehicleData.status
              : vehicleStatuses[vehicle.deviceId] || vehicleData.status;
            // const vehicleIcon = getVehicleIcon(
            //   vehicle.deviceId,
            //   vehicle?.heading,
            // );
            const direction = vehicleDirections[vehicle.deviceId];
            const vehicleIcon = getCustomVehicleIcon(
              vehicle.deviceId,
              vehicle?.heading || 0,
              direction,
              zoom,
            );
            const vehicleName = vehicleData.name;
            const isOffRoute = !snappedPositions[vehicle.deviceId];
            const currentSpeed = parseFloat(
              vehicle.speed || vehicle.Speed || 0,
            );
            // console.log(settings);
            const OVERSPEED_THRESHOLD = settings?.maxSpeed || 30;
            // console.log(
            //   `Vehicle ${vehicleName} speed: ${currentSpeed} km/h (threshold: ${OVERSPEED_THRESHOLD} km/h)`,
            //   { vehicle },
            // );
            const isOverSpeed = currentSpeed > OVERSPEED_THRESHOLD;
            return (
              vehicle &&
              vehicle.latitude &&
              vehicle.longitude && (
                <React.Fragment key={vehicle.deviceId}>
                  <OverlayView
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: "13px",
                        left: "0px",
                        width: "2px",
                        height: "2px",
                      }}
                    >
                      {(zoom === null || zoom >= 14) &&
                        (() => {
                          const topPx =
                            labelOffsets[vehicle.deviceId]?.topPx ?? 10;
                          // connector runs from icon centre (y=0) down to the label top
                          const connectorH = topPx; // label starts at topPx from anchor
                          const vehicleColor =
                            vehicleData.vehicle_color || "#008C23";
                          return (
                            <>
                              {/* Connector line: from icon anchor to label top */}
                              {connectorH > 4 && (
                                <svg
                                  style={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    top: "0px",
                                    pointerEvents: "none",
                                    overflow: "visible",
                                    zIndex: 999,
                                  }}
                                  width="2"
                                  height={connectorH}
                                >
                                  <line
                                    x1="1"
                                    y1="0"
                                    x2="1"
                                    y2={connectorH}
                                    stroke="black"
                                    strokeWidth="1.8"
                                    strokeDasharray="3 2"
                                  />
                                </svg>
                              )}
                              {/* Label */}
                              <div
                                style={{
                                  position: "absolute",
                                  top:
                                    labelOffsets[vehicle.deviceId]?.top ??
                                    "10px",
                                  left:
                                    labelOffsets[vehicle.deviceId]?.left ??
                                    "50%",
                                  transform:
                                    labelOffsets[vehicle.deviceId]?.transform ??
                                    "translateX(-50%)",
                                  color:
                                    status === "DEAD" || status === "INACTIVE"
                                      ? "#000"
                                      : "#fff",
                                  fontSize: "8px",
                                  fontWeight: 600,
                                  fontFamily: "Arial, sans-serif",
                                  lineHeight: "12px",
                                  padding: "6px",
                                  textAlign: "left",
                                  whiteSpace: "nowrap",
                                  backgroundColor:
                                    status === "DEAD"
                                      ? "#F9E719"
                                      : status === "INACTIVE"
                                        ? "#F9E719"
                                        : "#008C23",
                                  borderRadius: "10px",
                                  zIndex: 1000,
                                  border: `1.5px solid ${vehicleColor}`,
                                }}
                              >
                                {vehicleName}
                                <span style={{ fontWeight: "bold" }}>
                                  {getDirectionArrow(vehicle.deviceId)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                    </div>
                  </OverlayView>
                  <Marker
                    position={{
                      lat: parseFloat(vehicle.latitude),
                      lng: parseFloat(vehicle.longitude),
                    }}
                    icon={vehicleIcon}
                    onClick={() => openVehicleDetails(vehicle)}
                    onMouseOver={() => {
                      hoveredVehicleIdRef.current = vehicle.deviceId;
                      if (vehicleDetailsHoverTimeoutRef.current) {
                        clearTimeout(vehicleDetailsHoverTimeoutRef.current);
                      }

                      vehicleDetailsHoverTimeoutRef.current = setTimeout(() => {
                        if (hoveredVehicleIdRef.current === vehicle.deviceId) {
                          openVehicleDetails(vehicle);
                        }
                      }, 3000);
                    }}
                    onMouseOut={() => {
                      hoveredVehicleIdRef.current = null;
                      if (vehicleDetailsHoverTimeoutRef.current) {
                        clearTimeout(vehicleDetailsHoverTimeoutRef.current);
                        vehicleDetailsHoverTimeoutRef.current = null;
                      }
                      setSelectedVehicle(null);
                    }}
                  />
                  {isOverSpeed && (
                    <OverlayView
                      position={{
                        lat: parseFloat(vehicle.latitude),
                        lng: parseFloat(vehicle.longitude),
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: "relative",
                          top: "-10px",
                          left: "-10px",
                        }}
                      >
                        <div
                          title={`OVERSPEED: ${currentSpeed} km/h`}
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: "#FF0000",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #FFFFFF",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                            animation: "pulse 1.5s infinite",
                            zIndex: 1001,
                          }}
                        >
                          <span
                            style={{
                              color: "#FFFFFF",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            ⚠
                          </span>
                        </div>
                      </div>
                    </OverlayView>
                  )}
                </React.Fragment>
              )
            );
          })}

          {directions.map((direction, index) => (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: direction.routeColor || "#000000",
                  strokeOpacity: 0.8,
                  strokeWeight: 4,
                },
              }}
            />
          ))}
          <MapLegend
            showVehicle
            vehicleIconUrl={vehicleLegendIconUrl}
            statusItems={[
              { label: "Active", color: "#008C23", borderColor: "#008C23" },
              { label: "Inactive", color: "#F9E719", borderColor: "#aaa" },
            ]}
            routeColors={routeLegendColors}
            stopItems={stopLegendItems}
            placeItems={placeLegendItems}
            showDirection
          />
          {/* ── Vehicle Detail Popup ── */}
          {selectedVehicle && (
            <OverlayView
              position={{
                lat: parseFloat(selectedVehicle.latitude),
                lng: parseFloat(selectedVehicle.longitude),
              }}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                style={{
                  position: "absolute",
                  bottom: "60px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
                  width: "220px",
                  padding: "16px",
                  fontFamily: "Arial, sans-serif",
                  zIndex: 2000,
                }}
              >
                {/* Close button */}
                <button
                  onClick={() => setSelectedVehicle(null)}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "12px",
                    background: "none",
                    border: "none",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#888",
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>

                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                    paddingRight: "20px",
                  }}
                >
                  <span
                    style={{ fontWeight: 700, fontSize: "14px", color: "#111" }}
                  >
                    {selectedVehicle.name}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "3px 10px",
                      borderRadius: "20px",
                      border: `1.5px solid ${
                        selectedVehicle.status === "DEAD" ||
                        selectedVehicle.status === "INACTIVE"
                          ? "#F9A825"
                          : "#008C23"
                      }`,
                      color:
                        selectedVehicle.status === "DEAD" ||
                        selectedVehicle.status === "INACTIVE"
                          ? "#F9A825"
                          : "#008C23",
                    }}
                  >
                    {selectedVehicle.status === "DEAD" ||
                    selectedVehicle.status === "INACTIVE"
                      ? "Inactive"
                      : "Active"}
                  </span>
                </div>

                {/* Vehicle Image */}
                <div
                  style={{
                    width: "100%",
                    height: "100px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "14px",
                  }}
                >
                  {selectedVehicle.imageUrl ? (
                    <img
                      src={selectedVehicle.imageUrl}
                      alt={selectedVehicle.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <img
                      src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                        getRightToLeftBusIcon(
                          selectedVehicle.color || "green",
                          180,
                        ),
                      )}`}
                      alt="vehicle"
                      style={{ width: "80px", height: "80px" }}
                    />
                  )}
                </div>

                {/* Divider */}
                <div
                  style={{
                    borderTop: "1.5px solid #e0e0e0",
                    marginBottom: "12px",
                  }}
                />

                {/* Speed */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    fontSize: "12px",
                    color: "#444",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.99984 3.33301C5.40484 3.33301 1.66651 7.07134 1.66651 11.6663C1.66543 12.9525 1.96537 14.221 2.54234 15.3705C2.68401 15.6538 2.97234 15.833 3.28817 15.833H16.7107C17.0265 15.833 17.3157 15.6538 17.4573 15.3705C18.0343 14.221 18.3343 12.9525 18.3332 11.6663C18.3332 7.07134 14.5948 3.33301 9.99984 3.33301ZM14.1848 7.51801L10.8832 12.5497C10.7913 12.69 10.6727 12.8107 10.5341 12.9051C10.3955 12.9995 10.2397 13.0656 10.0755 13.0996C9.9113 13.1337 9.74202 13.135 9.57732 13.1036C9.41262 13.0721 9.25575 13.0085 9.11567 12.9163C8.9384 12.8004 8.79281 12.6422 8.69204 12.4558C8.59127 12.2695 8.5385 12.0611 8.5385 11.8493C8.5385 11.6374 8.59127 11.429 8.69204 11.2427C8.79281 11.0564 8.9384 10.8981 9.11567 10.7822L14.1473 7.48051C14.1534 7.47673 14.1608 7.47548 14.1678 7.47704C14.1748 7.4786 14.1809 7.48283 14.1848 7.48884C14.1875 7.49325 14.1889 7.49829 14.1889 7.50342C14.1889 7.50856 14.1875 7.5136 14.1848 7.51801Z"
                        fill="#647490"
                      />
                    </svg>
                  </span>
                  <span style={{ color: "#888" }}>Speed:</span>
                  <span style={{ fontWeight: 600, color: "#111" }}>
                    {selectedVehicle.speed
                      ? `${parseFloat(selectedVehicle.speed).toFixed(1)} Km/hr`
                      : "0 Km/hr"}
                  </span>
                </div>

                {/* Route */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "12px",
                    color: "#444",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>
                    <svg
                      width="27"
                      height="27"
                      viewBox="0 0 27 27"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.25 25.25C7.25 23.1167 9.25 21.2064 9.25 18.85C9.25 16.4936 7.45913 14.5833 5.25 14.5833C3.04087 14.5833 1.25 16.4936 1.25 18.85C1.25 21.2064 3.25 23.1167 5.25 25.25ZM5.25 25.25H20.5833C22.4243 25.25 23.9167 23.7576 23.9167 21.9167C23.9167 20.0757 22.4243 18.5833 20.5833 18.5833H17.25M21.25 11.9167C23.25 9.78333 25.25 7.87308 25.25 5.51667C25.25 3.16025 23.4592 1.25 21.25 1.25C19.0408 1.25 17.25 3.16025 17.25 5.51667C17.25 7.87308 19.25 9.78333 21.25 11.9167ZM21.25 11.9167H16.5833C14.7424 11.9167 13.25 13.4091 13.25 15.25C13.25 17.0909 14.7424 18.5833 16.5833 18.5833H18.05"
                        stroke="#647490"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <span style={{ color: "#888" }}>Route:</span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#111",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "110px",
                    }}
                  >
                    {selectedVehicle.routeName}
                  </span>
                </div>
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      </div>
      <style>
        {`
             @keyframes pulse {
               0%   { transform: scale(1);   opacity: 1; }
               50%  { transform: scale(1.2); opacity: 0.8; }
               100% { transform: scale(1);   opacity: 1; }
             }
           `}
      </style>
    </>
  );
}

export default React.memo(Map);
